/**
 * Utility functions for chat components
 */

/**
 * Get the data source based on the category
 * @param {string} category - The category of the instrument
 * @returns {string} - The data source
 */
export const getSourceByCategory = (category) => {
    const lowerCategory = category?.toLowerCase();
    return lowerCategory === "forex" || lowerCategory === "indices" 
        ? "oanda"
        : lowerCategory === "crypto" 
            ? "binance" 
            : lowerCategory === "stocks"
                ? "yahoo"
                : "oanda";
};

/**
 * Format markdown text to HTML
 * @param {string} text - The markdown text to format
 * @returns {string} - The formatted HTML
 */
export const formatMarkdown = (text) => {
    if (!text) return '';
    
    // Normalize line endings
    let formattedText = text.replace(/\r\n/g, '\n');
    
    // Pre-processing step: Look for numbered list items that appear to be part of a sequence
    // but have blank lines between them, and mark them for special processing
    formattedText = formattedText.replace(
        /^(\d+)\.\s+(.*?)$\n+^(\d+)\.\s+/gm,
        (match, num1, content, num2) => {
            // Check if these are sequential numbers
            if (parseInt(num2) === parseInt(num1) + 1) {
                return `${num1}. ${content}\n__LIST_CONTINUE__\n${num2}. `;
            }
            return match;
        }
    );
    
    // Step 1: Temporarily replace existing HTML links to protect them
    const htmlLinks = [];
    formattedText = formattedText.replace(/<a\s+(?:[^>]*?\s+)?href=(['"])(.*?)\1(?:[^>]*?\s+)?(?:target=(['"])(.*?)\3)?(?:[^>]*?\s+)?(?:rel=(['"])(.*?)\5)?[^>]*?>([\s\S]*?)<\/a>/gi, 
        (match) => {
            htmlLinks.push(match);
            return `__HTML_LINK_${htmlLinks.length - 1}__`;
        }
    );
    
    // Step 2: Escape HTML characters to prevent injection
    formattedText = formattedText
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    
    // Step 3: Handle code blocks first (to prevent formatting inside code)
    formattedText = formattedText.replace(/```([\s\S]*?)```/g, (match, codeContent) => {
        // Remove formatting inside code blocks
        return `<pre><code>${codeContent}</code></pre>`;
    });
    
    // Step 4: Handle inline code (again, to prevent formatting inside code)
    formattedText = formattedText.replace(/`([^`]+)`/g, (match, codeContent) => {
        return `<code>${codeContent}</code>`;
    });
    
    // Step 5: Handle headers (must process before bold/italic)
    formattedText = formattedText.replace(/^(#{1,6})\s+(.*?)$/gm, (match, hashes, content) => {
        const level = hashes.length;
        return `<h${level}>${content}</h${level}>`;
    });
    
    // Step 6: Handle bold
    formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Step 7: Handle italic
    formattedText = formattedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Step 8: Handle tables
    // Find table segments in the text
    const tableRegex = /^\|(.+)\|[\r\n]\|[-:| ]+\|[\r\n](\|.+\|[\r\n])+/gm;
    formattedText = formattedText.replace(tableRegex, (tableMatch) => {
        const rows = tableMatch.trim().split('\n');
        
        // Process header row
        const headerRow = rows[0];
        const headers = headerRow.split('|')
            .filter(cell => cell.trim().length > 0)
            .map(cell => `<th>${cell.trim()}</th>`)
            .join('');
            
        // Skip the separator row (row[1])
        
        // Process data rows
        const dataRows = rows.slice(2).map(row => {
            const cells = row.split('|')
                .filter(cell => cell.trim().length > 0)
                .map(cell => `<td>${cell.trim()}</td>`)
                .join('');
            return `<tr>${cells}</tr>`;
        }).join('');
        
        return `<table><thead><tr>${headers}</tr></thead><tbody>${dataRows}</tbody></table>`;
    });
    
    // Step 9: Replace our list continuation markers
    formattedText = formattedText.replace(/__LIST_CONTINUE__\n/g, '\n');
    
    // Step 10: Handle numbered lists
    // First identify numbered list items and wrap them individually
    formattedText = formattedText.replace(/^(\d+)\.[ \t]+(.*?)$/gm, '<li value="$1">$2</li>');
    
    // Join adjacent list items into a single list
    let lastIndex = -1;
    let inList = false;
    const lines = formattedText.split('\n');
    for (let i = 0; i < lines.length; i++) {
        if (lines[i].match(/<li value="\d+">/)) {
            if (!inList) {
                lines[i] = '<ol>' + lines[i];
                inList = true;
            }
            lastIndex = i;
        } else if (inList && i > lastIndex + 1) {
            // We've exited the list
            lines[lastIndex] = lines[lastIndex] + '</ol>';
            inList = false;
        }
    }
    
    // Close any open list at the end
    if (inList && lastIndex >= 0 && lastIndex < lines.length) {
        lines[lastIndex] = lines[lastIndex] + '</ol>';
    }
    
    formattedText = lines.join('\n');
    
    // Step 11: Handle unordered lists
    formattedText = formattedText.replace(/^[*-][ \t]+(.*?)$/gm, '<li>$1</li>');
    
    // Use the same approach for unordered lists
    lastIndex = -1;
    inList = false;
    const ulLines = formattedText.split('\n');
    for (let i = 0; i < ulLines.length; i++) {
        // Only match <li> tags that aren't already part of an ordered list
        if (ulLines[i].match(/<li>(?!value="\d+")/) && !ulLines[i].includes('<li value="')) {
            if (!inList) {
                ulLines[i] = '<ul>' + ulLines[i];
                inList = true;
            }
            lastIndex = i;
        } else if (inList && i > lastIndex + 1) {
            // We've exited the list
            ulLines[lastIndex] = ulLines[lastIndex] + '</ul>';
            inList = false;
        }
    }
    
    // Close any open list at the end
    if (inList && lastIndex >= 0 && lastIndex < ulLines.length) {
        ulLines[lastIndex] = ulLines[lastIndex] + '</ul>';
    }
    
    formattedText = ulLines.join('\n');
    
    // Step 12: Handle markdown links [text](url)
    formattedText = formattedText.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
        // Make sure URL is properly formatted and sanitized
        if (url.match(/^https?:\/\//i)) {
            return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
        } else if (url.match(/^mailto:/i)) {
            // Handle mailto links
            return `<a href="${url}">${text}</a>`;
        }
        // If URL doesn't start with http(s), assume it's http
        return `<a href="http://${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    });
    
    // Step 13: Handle plain URLs
    // This regex is more carefully constructed to avoid false positives
    const urlRegex = /\bhttps?:\/\/[-A-Za-z0-9+&@#/%?=~_|!:,.;]*[-A-Za-z0-9+&@#/%=~_|]/g;
    
    formattedText = formattedText.replace(urlRegex, (url) => {
        // Don't process URLs that are already part of an <a> tag
        const preceded = formattedText.substring(Math.max(0, formattedText.indexOf(url) - 15), formattedText.indexOf(url));
        if (preceded.includes('<a') && !preceded.includes('</a>')) {
            return url;
        }
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });
    
    // Step 14: Handle email addresses
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    
    formattedText = formattedText.replace(emailRegex, (email) => {
        // Skip if already in an anchor tag
        const preceded = formattedText.substring(Math.max(0, formattedText.indexOf(email) - 15), formattedText.indexOf(email));
        if (preceded.includes('<a') && !preceded.includes('</a>')) {
            return email;
        }
        return `<a href="mailto:${email}">${email}</a>`;
    });
    
    // Step 15: Handle line breaks - do this last
    formattedText = formattedText.replace(/\n\n/g, '<br><br>');
    formattedText = formattedText.replace(/\n/g, '<br>');

    // Step 16: Clean up line breaks around list markup to avoid oversized gaps
    formattedText = formattedText
        .replace(/<ul><br\s*\/?>/g, '<ul>')
        .replace(/<ol><br\s*\/?>/g, '<ol>')
        .replace(/<br\s*\/?><\/ul>/g, '</ul>')
        .replace(/<br\s*\/?><\/ol>/g, '</ol>')
        .replace(/<\/li><br\s*\/?><br\s*\/?>/g, '</li>')
        .replace(/<\/li><br\s*\/?>/g, '</li>');
    
    // Step 17: Restore HTML links
    htmlLinks.forEach((link, index) => {
        formattedText = formattedText.replace(`__HTML_LINK_${index}__`, link);
    });
    
    return formattedText;
};

/**
 * Replace market data placeholders in text
 * @param {string} text - The text to process
 * @returns {string} - The processed text
 */
export const replaceMarketDataWithPlaceholder = (text) => {
    return text.replace(/\[(Market Data Included)\]/, '')
               .replace(/\[(Screenshot Included)\]/, '');
};

/**
 * Format a timestamp to a readable time
 * @param {string} timestamp - The timestamp to format
 * @returns {string} - The formatted time
 */
export const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';

    const now = new Date();
    const isSameDay =
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate();

    const timeString = date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
    });

    if (isSameDay) {
        return timeString;
    }

    const dateString = date.toLocaleDateString([], {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric'
    });

    return `${dateString} · ${timeString}`;
};

/**
 * Get the context message for the chat header
 * @param {string} selectedInstrument - The selected instrument
 * @param {string} selectedCategory - The selected category
 * @returns {string|null} - The context message or null
 */
export const getContextMessage = (selectedInstrument, selectedCategory) => {
    if (selectedInstrument) {
        return `${selectedCategory} ${selectedInstrument}`;
    }
    return null;
};

/**
 * For testing the markdown parser during development
 */
export const testMarkdownParser = () => {
    const testCases = [
        {
            input: "Hello **world** with *italic*",
            expected: "Hello <strong>world</strong> with <em>italic</em>"
        },
        {
            input: "# Header 1\n## Header 2",
            expected: "<h1>Header 1</h1><br><h2>Header 2</h2>"
        },
        {
            input: "Check out [Google](https://www.google.com)",
            expected: 'Check out <a href="https://www.google.com" target="_blank" rel="noopener noreferrer">Google</a>'
        },
        {
            input: "Check out https://www.google.com",
            expected: 'Check out <a href="https://www.google.com" target="_blank" rel="noopener noreferrer">https://www.google.com</a>'
        },
        {
            input: "```\nconst x = 5;\nconsole.log(x);\n```",
            expected: "<pre><code>\nconst x = 5;\nconsole.log(x);\n</code></pre>"
        },
        {
            input: "Inline `code` here",
            expected: "Inline <code>code</code> here"
        },
        {
            input: "- Item 1\n- Item 2\n1. Numbered 1\n2. Numbered 2",
            expected: "<ul><li>Item 1</li><br><li>Item 2</li></ul><ol><li value=\"1\">Numbered 1</li><br><li value=\"2\">Numbered 2</li></ol>"
        },
        {
            input: 'Existing HTML link <a href="https://example.com" target="_blank">Example</a>',
            expected: 'Existing HTML link <a href="https://example.com" target="_blank">Example</a>'
        },
        {
            input: 'Mixed content with <a href="https://example.com">HTML link</a> and [Markdown link](https://markdown.com)',
            expected: 'Mixed content with <a href="https://example.com">HTML link</a> and <a href="https://markdown.com" target="_blank" rel="noopener noreferrer">Markdown link</a>'
        },
        {
            input: 'HTML link <a href="https://example.com">Example</a> and plain URL https://plain.com',
            expected: 'HTML link <a href="https://example.com">Example</a> and plain URL <a href="https://plain.com" target="_blank" rel="noopener noreferrer">https://plain.com</a>'
        },
        {
            input: 'Contact me at user@example.com for more info',
            expected: 'Contact me at <a href="mailto:user@example.com">user@example.com</a> for more info'
        },
        {
            input: '[Email me](mailto:user@example.com) for more information',
            expected: '<a href="mailto:user@example.com">Email me</a> for more information'
        },
        {
            input: 'Email with dots: john.doe@example.com and hyphens: user-name@example.com',
            expected: 'Email with dots: <a href="mailto:john.doe@example.com">john.doe@example.com</a> and hyphens: <a href="mailto:user-name@example.com">user-name@example.com</a>'
        },
        {
            input: `| Header 1 | Header 2 |
| -------- | -------- |
| Cell 1   | Cell 2   |
| Cell 3   | Cell 4   |`,
            expected: '<table><thead><tr><th>Header 1</th><th>Header 2</th></tr></thead><tbody><tr><td>Cell 1</td><td>Cell 2</td></tr><tr><td>Cell 3</td><td>Cell 4</td></tr></tbody></table>'
        },
        {
            input: `| Left | Center | Right |
| :--- | :----: | ----: |
| 1    | 2      | 3     |`,
            expected: '<table><thead><tr><th>Left</th><th>Center</th><th>Right</th></tr></thead><tbody><tr><td>1</td><td>2</td><td>3</td></tr></tbody></table>'
        },
        {
            // Test for numbered lists with text between items
            input: `1. Market Cap: $822.24 billion

2. Revenue (TTM): $95.72 billion

3. Net Income: $6.11 billion`,
            expected: '<ol><li value="1">Market Cap: $822.24 billion</li></ol><br><ol><li value="2">Revenue (TTM): $95.72 billion</li></ol><br><ol><li value="3">Net Income: $6.11 billion</li></ol>'
        },
        {
            // Test for numbered lists with text between items but identified as sequential
            input: `Here are some key fundamentals:

1. Market Cap: $822.24 billion

2. Revenue (TTM): $95.72 billion

3. Net Income: $6.11 billion`,
            expected: 'Here are some key fundamentals:<br><br><ol><li value="1">Market Cap: $822.24 billion</li><br><li value="2">Revenue (TTM): $95.72 billion</li><br><li value="3">Net Income: $6.11 billion</li></ol>'
        }
    ];

    console.group("Markdown Parser Tests");
    let passCount = 0;
    testCases.forEach((test, index) => {
        const result = formatMarkdown(test.input);
        const passed = result.includes(test.expected);
        console.log(`Test ${index + 1}: ${passed ? '✅ PASSED' : '❌ FAILED'}`);
        if (!passed) {
            console.log('Input:', test.input);
            console.log('Expected:', test.expected);
            console.log('Result:', result);
        } else {
            passCount++;
        }
    });
    console.log(`${passCount}/${testCases.length} tests passed`);
    console.groupEnd();
};
